import React, { useState, useEffect, useRef, Fragment } from 'react';
import {
  sendClickedObjectIDsAfterSearch,
  sendNoResultsEvent,
} from '../util/AlgoliaInsights';
import { renderBlocks } from '../util/BlockUtil';

import {
  assemble,
  correctUrlFormatForRouting,
  correctDomainForLanguage,
  fetchData,
  isLinkAbsolute,
  marketoOptions as options,
  usageOptions,
  createFilterData,
  createFilterDataForResources,
  getAlgoliaDatasetValue,
  addQueryParamWithoutRedirecting,
  isItUndefined,
  getAlgoliaIndex,
  getCardImageSrc,
  createFiltersFromRedirect,
  noTruncationForAllScreen,
  pushSearchSuggestionClickEvent,
} from '../util/DataUtil';
import ArticleThumbnailCard from '../components/shared/ArticleThumbnailCard';
import PaginationLink from '../components/shared/PaginationLink';
import {
  buildPageHref,
  buildSearchHref,
  getPageFromQuery,
  getSearchFromQuery,
  hasMoreAlgoliaPages,
  stripListingParams,
} from '../util/PaginationUtil';
import { useWindowSize } from '../util/PageUtil';
import { useRouter } from 'next/router';
import { Constants } from '../util/Constants';
import dynamic from 'next/dynamic';
import { useDispatch, useSelector } from 'react-redux';
import { setLocalStorageRedux } from '../store/localStorageSlice';
import { CircularProgress } from '@mui/material';
import { SearchBarPlacement } from '../constants/SearchBarPlacement';
import { FilterName } from '../constants/FilterName';

const ResourceWatch = dynamic(
  () => import('../components/resources/resource-watch')
);
const ResourcePopular = dynamic(
  () => import('../components/resources/resource-popular')
);
const ResourceTitle = dynamic(
  () => import('../components/resources/resource-title')
);
const ResourceLatest = dynamic(
  () => import('../components/resources/resource-latest')
);
const ResourceContent = dynamic(
  () => import('../components/resources/resource-content')
);
const ResourceFilter = dynamic(
  () => import('../components/resources/resource-filter')
);

export const Resources = ({ data, width, posts, global, lang }) => {
  const router = useRouter();
  const windowSize = useWindowSize();
  // Seeded from the URL so a reload or a shared link restores the same search,
  // and so pagination can sit on top of it (`?search=cloud&page=2`).
  const [query, setQuery] = useState(() => {
    const term = getSearchFromQuery(router?.query);
    return term ? [term] : [];
  });
  // Seeded from the URL so a direct hit on `?page=3` opens page three rather
  // than silently showing page one with a Show More link pointing at page two.
  const [page, setPage] = useState(() => getPageFromQuery(router?.query));
  const [facetFilters, setFacetFilters] = useState([]);
  const [languageFilter, setLanguageFilter] = useState([]);
  const [results, setResults] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  // Server-rendered first slice. Carries hits plus nbPages/nbHits, which is what
  // the Show More condition below is driven by.
  const serverResults = data?.initialResources;
  const serverHits = Array.isArray(serverResults?.hits) ? serverResults.hits : [];

  const [algoliaData, setAlgoliaData] = useState(serverResults || []);
  const [filterProps, setFilterProps] = useState([]);
  const [showMore, setShowMore] = useState(serverHits);
  const [topSearchItems, setTopSearchItems] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [closeFilters, setCloseFilters] = useState(false);
  const [queryID, setQueryID] = useState(null);
  const noResultsTrackedRef = useRef(null);
  const myLocalStorage = useSelector(setLocalStorageRedux);

  let hitsPerPage = 16;
  let lng = lang;
  let facetFiltersArray = [];
  let lngArray = [];

  // The first slice is already on the page from the server. Both effects below
  // run on mount and would otherwise refetch it — the first replacing it, the
  // second appending it a second time — so the first pass of each is skipped
  // when there is server data to skip for.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialFetch = useRef(serverHits.length > 0 && !urlNarrowsResults);
  const skipInitialPagination = useRef(true);

  useEffect(() => {
    if (!lng) {
      return;
    }

    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }

    setIsLoading(true);
    fetchAlgoliaData();
    // getMostPopularSearchTerms();
  }, [applyFilter, query, filterProps]);

  useEffect(() => {
    getMostPopularSearchTerms();
  }, []);

  useEffect(() => {
    if (!lng) {
      return;
    }

    if (skipInitialPagination.current) {
      skipInitialPagination.current = false;
      return;
    }

    if (page > 0) {
      fetchAlgoliaPagination();
    }
  }, [page]);

  // Hydrating the filters out of the URL is a one-time thing. This effect
  // re-runs whenever its dependencies settle — the author list arriving, for
  // instance — and on a later run it would read the URL again and re-apply it
  // on top of whatever the visitor had since selected, overwriting their choice
  // with a fresh mapping. That looked like the filter being applied and then
  // quietly ignored.
  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    // `page` is in the query string but is not a filter. Letting it through
    // makes the check below think the visitor arrived with filters applied,
    // which resets the page to one — so reloading `?page=2` showed page one.
    let localFilter = stripListingParams(router.query);

    if (typeof window !== 'undefined') {
      // localFilter.length > 0 ? triggerFilterFromRedirect([{name: localFilter}]) : clearAll()
      //
      // The URL still carries filters, so there is nothing to clear — whether
      // or not this run is the one that hydrated them. Reaching the clear
      // branch on a later run undid the filter the URL had just asked for.
      if (hasNonEmptyProperties(localFilter)) {
        if (!hydratedFromUrl.current) {
          hydratedFromUrl.current = true;
          triggerFilterFromRedirect(localFilter);
        }
      } else if (filterProps.length > 0 || query.length > 0) {
        // Only clear when something is actually applied.
        //
        // `clearAll` resets the page through `triggerFilter([])`, and this
        // effect re-runs whenever its dependencies settle — the author list
        // arriving, for instance. Guarding only the first run was not enough:
        // the second run wiped the page the URL had asked for, so after a
        // reload of `?page=3` the See More link pointed back at page two.
        //
        // With nothing applied there is nothing to clear, so the page the
        // visitor asked for survives.
        clearAll();
      }

    }
  }, [myLocalStorage]);

  useEffect(() => {
    if (!algoliaData || algoliaData.nbHits !== 0 || !queryID) return;
    if (noResultsTrackedRef.current === queryID) return;
    noResultsTrackedRef.current = queryID;
    sendNoResultsEvent(queryID, getAlgoliaDatasetValue(0) + getAlgoliaIndex());
  }, [algoliaData, queryID]);

  function hasNonEmptyProperties(obj) {
    for (const key in obj) {
      if (
        key !== 'url' &&
        obj[key] !== null &&
        obj[key] !== undefined &&
        obj[key] !== ''
      ) {
        return true;
      }
    }
    return false;
  }

  const fetchAlgoliaData = () => {
    setResults(
      (query.length || facetFilters.length || languageFilter.length) !== 0
    );

    let url = createFilterDataForResources(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      getAlgoliaDatasetValue(0) + getAlgoliaIndex()
    );

    const fetchAlgolia = async () => {
      try {
        const response = await fetch(url, options);
        const newResponse = await response.json();
        const filteredHits = newResponse.hits.filter(
          (item) => !item.field_exclude_from_resource_sect
        );
        setShowMore(filterOutNoContentType(filteredHits));
        setAlgoliaData(newResponse);
        setQueryID(newResponse.queryID ?? null);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching Algolia data:', error);
      }
    };
    fetchAlgolia();
  };

  const fetchAlgoliaPagination = () => {
    let url = createFilterDataForResources(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      getAlgoliaDatasetValue(0) + getAlgoliaIndex()
    );

    const fetchAlgolia = async () => {
      await fetch(url, options)
        .then((response) => response.json())
        .then((newResponse) => {
          setShowMore((previousResponse) => [
            ...previousResponse,
            ...newResponse.hits,
          ]);
          setAlgoliaData(newResponse);
          setQueryID(newResponse.queryID ?? null);
        });
    };

    fetchAlgolia();
  };

  const filterOutNoContentType = (data) =>
    data.filter((item) => item.content_type !== undefined);

  const fetchAlgoliaMostPopular = (queryParam) => {
    let lang = JSON.stringify('search_api_language:' + lng);
    // let url = 'https://SWULV5ONQ5-dsn.algolia.net/1/indexes/drupal?query=' + queryParam + '&facetFilters=[' + lang + ']&hitsPerPage=3&page=0';
    // let url = 'https://O2VJB2KMPB-dsn.algolia.net/1/indexes/drupalprod?query=' + queryParam + '&facetFilters=[' + lang + ']&hitsPerPage=3&page=0';
    // let url = Constants.algoliaProduction + 'drupalprod?query=' + queryParam + '&facetFilters=[' + lang + ',["moderation_state:published"]]&hitsPerPage=3&page=0';
    let url =
      Constants.algoliaProduction +
      'drupal' +
      getAlgoliaIndex() +
      '?query=' +
      queryParam +
      '&facetFilters=[' +
      lang +
      ',["status:true"]]&hitsPerPage=3&page=0';
    // let url = Constants.algoliaProduction + 'drupalprod?query=' + queryParam + '&facetFilters=[' + lang + ']&hitsPerPage=3&page=0';

    const fetchData = async () => {
      try {
        const response = await fetch(url, options);
        const newResponse = await response.json();
        const filteredHits = newResponse.hits.filter(
          (item) => !item.field_exclude_from_resource_sect
        );
        setTopSearchItems((previousResponse) => [
          ...previousResponse,
          ...filteredHits,
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    // setFilterProps([]);
    // setFacetFilters([]);
    setPage(0);
    setApplyFilter(false);

    // Reflect the term in the address bar. `replace` rather than `push` so
    // typing does not leave one history entry per keystroke, and shallow so
    // Next does not re-run data fetching we are about to do ourselves.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      if (filter.field_source_translation === 'articles') {
        router.push({
          pathname: '/resources/blog',
        });
      }
      searchFilters.push(filter?.field_source_translation);
    }

    setFacetFilters(searchFilters);
    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
    setApplyFilter(false);
  };

  // const handleClick = (event, urlAlias) => {
  //     event.stopPropagation();
  //     if(urlAlias.url.length !== 0){
  //         if (isLinkAbsolute(correctDomainForLanguage(urlAlias.language)+urlAlias.url)){
  //             window.open(correctDomainForLanguage(urlAlias.language)+urlAlias.url, '_blank');
  //         }else{
  //             router.push(correctDomainForLanguage(urlAlias.language)+urlAlias.url);
  //         }
  //     }
  // }

  const triggerFilterFromRedirect = (filter) => {
    let filters = createFiltersFromRedirect(filter, data?.categories);

    if (filters?.length === 0 && typeof filter?.category === 'string') {
      var newFilter = {
        field_source_translation: filter?.category,
        name: filter?.category,
      };
      filters.push(newFilter);
    }
    triggerFilter(filters, false);
    setApplyFilter(true);
    // setQuery([]);
  };

  const handleChipClick = (event, filter) => {
    event.preventDefault();
    event.stopPropagation();

    let url = '';

    if (typeof window !== undefined) {
      url = window.location.pathname;
    }

    for (let item of data?.categories) {
      if (item?.name === filter[0]?.name) {
        addQueryParamWithoutRedirecting(router, url, filter[0]?.name, 'type');
        triggerFilter([item]);
      }
    }

    setApplyFilter(true);
    // setQuery([]);
  };

  const renderCategories = (categories) => {
    if (typeof categories == 'object') {
      return categories.map((item, index) => {
        return (
          <div
            key={`category-${item || 'category'}`}
            onClick={(event) => handleChipClick(event, [{ name: item }])}
          >
            {item}
          </div>
        );
      });
    } else {
      return categories;
    }
  };

  // Routed href string for the shared search card (correctUrlFormatForRouting
  // returns { url, language } — the card needs a plain string).
  const getSearchCardHref = (item) => {
    if (!item?.url) return '/';
    const routed = correctUrlFormatForRouting(item.url);
    return routed?.url || '/';
  };

  // Subheader meta for the shared search card: content type + time to read, in
  // the same blog-info structure the other Resource areas use (no author/date
  // byline is added — Resources search doesn't have one).
  const renderSearchMeta = (item) => {
    const label = item?.content_type;
    const time = item?.field_time_to_read;
    if (!label && !time) return null;
    return (
      <div className={'blog-info'}>
        {label ? <span className={'info-item'}>{label}</span> : null}
        {label && time ? (
          <span className={'separator separator-inline info-item'}>
            {' | '}
          </span>
        ) : null}
        {time ? (
          <span className={'blog-read-time info-item'}>{time}</span>
        ) : null}
      </div>
    );
  };

  const clearAll = () => {
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    // setQuery([]);
    if (query.length > 0) {
      setQuery([]);
    }
    setShowMore([]);
    setCloseFilters(false);
  };

  const clearQuery = () => {
    setQuery([]);
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setIsSearchActive(false);
    setIsFilterActive(true);
  };

  const getMostPopularSearchTerms = () => {
    // fetch('https://analytics.algolia.com/2/searches?index=drupal', options)
    fetch(
      'https://analytics.algolia.com/2/searches?index=drupalprod',
      usageOptions
    )
      .then((response) => response.json())
      .then((actualData) => {
        getTopSearchesFromAlgolia(actualData);
      });
  };

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getTopSearchesFromAlgolia = (searchTerms = []) => {
    if (!searchTerms) {
      return;
    }

    for (let item of searchTerms.searches) {
      if (item.nbHits !== 0) {
        fetchAlgoliaMostPopular(item.search);
      }
    }
  };

  const renderSearchCards = (data, length = -1) => {
    let result = [];

    if (!data) {
      return;
    }

    if (data.length === 0) {
      result = [];
    }

    if (length !== -1) {
      data = [...new Set(data)].slice(0, length);
    }

    for (const [index, item] of data.entries()) {
      // Resources search results now render from the shared ArticleThumbnailCard
      // so typography, tag chips and hover match Blog. The grid layout stays as
      // is via the `search-card` class (widths/margins in SCSS). No author/date
      // byline is added; the meta line keeps the existing content type + time.
      const cardTitle = item?.field_template_title
        ? noTruncationForAllScreen(item?.field_template_title, 42, 55, 55, 30)
        : noTruncationForAllScreen(item?.title, 42, 60, 60, 30);

      result.push(
        <ArticleThumbnailCard
          key={item.objectID || index}
          className="search-card"
          href={getSearchCardHref(item)}
          image={getCardImageSrc(item)}
          imageAlt="search-image"
          header={cardTitle}
          subheader={renderSearchMeta(item)}
          primaryCategory={
            item?.category_name
              ? {
                  name: Array.isArray(item.category_name)
                    ? item.category_name[0]
                    : item.category_name,
                }
              : null
          }
          secondaryCategories={[]
            .concat(item?.second_category || [])
            .map((name) => ({ name }))}
          onTagClick={(event, tag) => {
            handleChipClick(event, [{ name: tag?.name || '' }]);
            handleScroll();
          }}
          onClick={() => {
            pushSearchSuggestionClickEvent(
              Array.isArray(query)
                ? (query[0]?.toLowerCase() ?? '')
                : (query?.toLowerCase() ?? ''),
              SearchBarPlacement.RESOURCES,
              cardTitle,
              index + 1
            );
            sendClickedObjectIDsAfterSearch(
              queryID,
              item.objectID,
              getAlgoliaDatasetValue(0) + getAlgoliaIndex(),
              index + 1
            );
          }}
        />
      );
    }

    return result;
  };

  function renderSearchResults(data, global) {
    if (!data) {
      return;
    }

    if (data.length === 0) {
      return (
        <div className={'no-result'}>
          <h2>
            {global?.field_no_results_text?.[0]?.value ||
              'No results were found. Please check the spelling, reset any filters and try again.'}
          </h2>
          <div
            className={'clear-all'}
            onClick={() => {
              clearAll();
            }}
          >
            {global?.field_clear_all_label?.[0]?.value ||
              'Clear all search terms.'}
          </div>
          <div className={'most-popular'}>
            <h2>
              {global?.field_most_popular_text?.[0]?.value || 'Most popular'}
            </h2>
            <div className={'search-results'}>
              {renderSearchCards(topSearchItems, 12)}
            </div>
          </div>
        </div>
      );
    }

    return renderSearchCards(data);
  }
  // console.log(data);
  return (
    <div className={'resource resources_page'}>
      {/* <ResourceTitle
        title={isItUndefined(data?.field_resources_title)}
        subtitle={isItUndefined(data?.field_resources_subtitle)}
        placeholder={'Search All Resources...'}
      /> */}

      <div className="resources_content_blocks">
        {renderBlocks(
          results && (filterProps.length !== 0 || query.length > 0)
            ? {
                field_template_blocks: [
                  ...(data?.field_template_blocks?.[0]
                    ? [data.field_template_blocks[0]]
                    : []),
                  { resource_filter: { value: 'val' } },
                ],
                content_type: 'resources_page',
              }
            : data,
          (width = { width }),
          lng,
          global,
          [],
          [],
          [],
          handleChipClick,
          data?.latest,
          <ResourceFilter
            metrosLabel={isItUndefined(global?.field_metro_label) || 'Metros'}
            key="resource-filter"
            clearQuery={clearQuery}
            isSearchActive={isSearchActive}
            isFilterActive={isFilterActive}
            setIsSearchActive={setIsSearchActive}
            setIsFilterActive={setIsFilterActive}
            search={data?.field_search_resources[0]?.value}
            title={data?.field_filter_resources[0]?.value}
            filterProps={filterProps}
            triggerFilter={triggerFilter}
            data={assemble(data?.categories)}
            onSearchChange={handleSearch}
            handleFilterApply={setApplyFilter}
            searchQuery={query}
            setQuery={setQuery}
            languageFilter={languageFilter}
            setLanguageFilter={setLanguageFilter}
            screenWidth={windowSize.width}
            clearAll={clearAll}
            closeFilters={closeFilters}
            setCloseFilters={setCloseFilters}
            cancelText={isItUndefined(global?.field_cancel_text_translation)}
            applyFilterText={isItUndefined(global?.field_apply_filters_text)}
            clearAllText={isItUndefined(global?.field_clear_all_text)}
            selectedText={isItUndefined(global?.field_selected_text)}
            seeMoreText={isItUndefined(global?.field_see_more_text)}
            seeLessText={isItUndefined(global?.field_see_less)}
            placement={SearchBarPlacement.RESOURCES}
            dataLayerFilterName={FilterName.RESOURCES}
          />
        )}
      </div>
      {results && (filterProps.length !== 0 || query.length > 0) ? (
        <div className={'search-results-header inner-container'}>
          <div className="inner-container">
            <h2>{global?.field_results_label?.[0]?.value || 'Results'}</h2>
            <div className={'horizontal-line'} />
            {isLoading ? (
              <div className={'loader'}>
                <CircularProgress />
              </div>
            ) : (
              <Fragment>
                <div className={'search-results'}>
                  {renderSearchResults(showMore, global, data)}
                </div>
                {hasMoreAlgoliaPages(algoliaData) && (
                    <PaginationLink page={page + 1} onLoadMore={setPage}>
                      {global?.field_see_more_text?.[0]?.value || 'See More'}
                    </PaginationLink>
                  )}
              </Fragment>
            )}
          </div>
        </div>
      ) : (
        <>
          {/*  <div className='resources_content_blocks'>*/}
          {/*    {renderBlocks(*/}
          {/*    data,*/}
          {/*    width={width},*/}
          {/*    lng,*/}
          {/*    global,*/}
          {/*    [],*/}
          {/*    [],*/}
          {/*    [])}*/}
          {/*</div>*/}
          {/* {data?.featured ? (
            <ResourceContent
              global={global}
              title={isItUndefined(data?.field_featured_title)}
              featured={data?.featured}
              onChipClick={handleChipClick}
              width={width}
            />
          ) : null} */}
          {/* {data?.latest ? (
            <ResourceLatest
              title={isItUndefined(data?.field_latest_insights)}
              latest={data?.latest}
              onChipClick={handleChipClick}
            />
          ) : null} */}
          {/* <ResourceWatch data={data?.mutlimedia_block[0]} width={width} /> */}
          <ResourcePopular
            title={isItUndefined(data?.field_popular_downloads)}
            posts={posts}
            onChipClick={handleChipClick}
            width={width}
          />
        </>
      )}
    </div>
  );
};

export async function categoriesProps(endpoint, lang) {
  return await fetchData(endpoint, 'template-categories?lang=' + lang);
}

export async function popularDownloadsProps(endpoint, lang) {
  const res = await fetch(
    endpoint.apiV2Url + 'popular-downloads?langcode=' + lang,
    Constants.headers
  );
  // return fetchData('popular-downloads?lang+' + lang)
  return await res.json();
}

export async function popularDownloadsBlockProps(
  endpoint,
  lang,
  cats,
  count,
  type,
  dupes,
  nodeId
) {
  const res = await fetch(
    endpoint.apiV2Url +
      'popular-downloads-tid?tid=' +
      cats +
      '&langcode=' +
      lang +
      '&count=' +
      count +
      '&case=' +
      type +
      '&dupes=' +
      dupes,
    Constants.headers
  );
  // return fetchData('popular-downloads?lang+' + lang)
  return await res.json();
}

// export async function featuredProps(data) {
//     return fetchData('templates?featured=1&lang=' + data.langcode);
// }

export async function latestProps(endpoint, lang) {
  const res = await fetch(
    endpoint.apiV2Url + 'latest-insights?langcode=' + lang,
    Constants.headers
  );
  return await res.json();
}

export async function productFamilyCategoriesProps(endpoint, lang) {
  return await fetchData(endpoint, 'product-family-categories?lang=' + lang);
}
